import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMouseParallax } from "../../utils/mouseParallax";
// Import Swiper components and styles
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

const processSteps = [
    {
        number: "01",
        duration: "1h",
        title: "Kick Off Meeting",
        description:
            "Première rencontre pour comprendre vos attentes et définir ensemble les objectifs du projet.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                viewBox="0 0 24 25"
                fill="none"
                className="steps_card_icon"
            >
                <path
                    d="M20 13V6C20 4.89543 19.1046 4 18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20H12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M12 11H16V15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M18 22C20.2091 22 22 20.2091 22 18C22 15.7909 20.2091 14 18 14C15.7909 14 14 15.7909 14 18C14 20.2091 15.7909 22 18 22Z"
                    stroke="currentColor"
                    strokeWidth="2"
                />
            </svg>
        ),
        deliverables: ["Brief projet", "Devis", "Planning", "Équipe"],
    },
    {
        number: "02",
        duration: "2-3 jours",
        title: "Découverte du projet",
        description:
            "Analyse en profondeur de vos besoins, de votre marché et de vos concurrents pour établir une stratégie efficace.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                viewBox="0 0 24 25"
                fill="none"
                className="steps_card_icon"
            >
                <path
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        ),
        deliverables: ["Audit", "Benchmark", "Personas", "Objectifs"],
    },
    {
        number: "03",
        duration: "3-5 jours",
        title: "Structure du projet",
        description:
            "Élaboration de l'architecture d'information et des wireframes pour valider la structure et la navigation du projet.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                viewBox="0 0 24 25"
                fill="none"
                className="steps_card_icon"
            >
                <path
                    d="M2 9H22"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M6 21H18C19.1046 21 20 20.1046 20 19V5C20 3.89543 19.1046 3 18 3H6C4.89543 3 4 3.89543 4 5V19C4 20.1046 4.89543 21 6 21Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M12 12V17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M12 17L14 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M12 17L10 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        ),
        deliverables: [
            "Arborescence",
            "Wireframes",
            "Prototypes Lo-Fi",
            "User Stories",
        ],
    },
    {
        number: "04",
        duration: "1 semaine",
        title: "Design",
        description:
            "Création de l'identité visuelle et des maquettes graphiques pour donner vie à votre projet avec une esthétique unique.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                viewBox="0 0 24 25"
                fill="none"
                className="steps_card_icon"
            >
                <path
                    d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        ),
        deliverables: [
            "Maquettes UI",
            "Design System",
            "Prototypes Hi-Fi",
            "Assets",
        ],
    },
    {
        number: "05",
        duration: "2-3 semaines",
        title: "Développement",
        description:
            "Intégration et développement de la solution en suivant les meilleures pratiques pour garantir qualité et performances.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                viewBox="0 0 24 25"
                fill="none"
                className="steps_card_icon"
            >
                <path
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        ),
        deliverables: ["Front-end", "Back-end", "API", "Base de données"],
    },
    {
        number: "06",
        duration: "2-3 jours",
        title: "Tests approfondis",
        description:
            "Phase de tests et d'optimisation pour s'assurer de la robustesse, de la sécurité et de la performance de la solution.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                viewBox="0 0 24 25"
                fill="none"
                className="steps_card_icon"
            >
                <path
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        ),
        deliverables: [
            "Tests unitaires",
            "Tests d'intégration",
            "Optimisation",
            "Sécurité",
        ],
    },
    {
        number: "07",
        duration: "1-2 jours",
        title: "Déploiement",
        description:
            "Mise en production de votre projet sur vos serveurs ou dans le cloud avec configuration des environnements.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                viewBox="0 0 24 25"
                fill="none"
                className="steps_card_icon"
            >
                <path
                    d="M22 12H16L14 15H10L8 12H2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M5.5 5C7.5 3 9 2 12 2C15 2 16.5 3 18.5 5C20.5 7 22 8.5 22 12C22 15.5 20.5 17 18.5 19C16.5 21 15 22 12 22C9 22 7.5 21 5.5 19C3.5 17 2 15.5 2 12C2 8.5 3.5 7 5.5 5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        ),
        deliverables: [
            "Mise en production",
            "Configuration",
            "Documentation",
            "Recette",
        ],
    },
    {
        number: "08",
        duration: "1 mois",
        title: "Support",
        description:
            "Accompagnement post-lancement avec maintenance, évolution et assistance technique pour pérenniser votre projet.",
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                viewBox="0 0 24 25"
                fill="none"
                className="steps_card_icon"
            >
                <path
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        ),
        deliverables: ["Maintenance", "Mises à jour", "Support", "Formation"],
    },
];

const ProcessCard = ({ step, isActive }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        // Outer container with padding to ensure the scaled card doesn't overflow
        <div className="p-2 relative h-full">
            {/* Motion div that handles hover and animation */}
            <motion.div
                className="relative w-full h-full"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                whileHover={{
                    scale: 1.04,
                    transition: { duration: 0.2, ease: "easeOut" },
                }}
            >
                {/* Gradient border container - larger than the content to account for scaling */}
                <div
                    className={`absolute inset-[-3px] rounded-xl transition-opacity duration-300 ${
                        isHovered ? "opacity-100" : "opacity-0"
                    }`}
                    style={{
                        background:
                            "linear-gradient(to right, #3b82f6, #8b5cf6, #ec4899)",
                    }}
                ></div>

                {/* White card content - same size as before but with the gradient border outside */}
                <div className="relative bg-white rounded-[12px] h-full flex flex-col shadow-[0_10px_30px_-15px_rgba(0,0,0,0.3)] hover:shadow-[0_15px_35px_-10px_rgba(0,0,0,0.35)] hover:bg-gray-50 transition-all duration-200 ease-out p-5">
                    {/* Header with number and duration */}
                    <div className="flex items-center justify-between mb-3">
                        <div
                            className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-medium transition-all duration-300 transform ${
                                isHovered || isActive
                                    ? "bg-black scale-110"
                                    : ""
                            }`}
                        >
                            {step.number}
                        </div>
                        <span
                            className={`text-sm italic font-medium px-3 py-1.5 transition-all duration-300 ${
                                isHovered || isActive ? "text-black" : ""
                            }`}
                        >
                            {step.duration}
                        </span>
                    </div>

                    {/* Title with text-gradient on hover */}
                    <h3
                        className={`text-xl font-bold mb-3 transition-all duration-300 ${
                            isHovered || isActive ? "text-black/80" : ""
                        }`}
                    >
                        {step.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 text-sm flex-grow">
                        {step.description}
                    </p>

                    {/* New Deliverables Section - Elegant list with icons */}
                    <div className="mt-auto">
                        <div className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-gray-100 pt-5">
                            {step.deliverables.map((deliverable, idx) => (
                                <motion.div
                                    key={idx}
                                    className="flex items-center"
                                    initial={{ opacity: 0.8 }}
                                    animate={{
                                        opacity:
                                            isHovered || isActive ? 1 : 0.8,
                                        x:
                                            isHovered || isActive
                                                ? [0, 3, 0]
                                                : 0,
                                    }}
                                    transition={{
                                        duration: 0.3,
                                        delay: idx * 0.05,
                                        ease: "easeInOut",
                                    }}
                                >
                                    <div
                                        className={`w-1.5 h-1.5 rounded-full ${
                                            isHovered || isActive
                                                ? "bg-gradient"
                                                : "bg-gray-400"
                                        } mr-2 flex-shrink-0`}
                                    ></div>
                                    <span className="text-xs text-gray-700">
                                        {deliverable}
                                    </span>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

const ProcessSection = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [slidesPerView, setSlidesPerView] = useState(4.15); // État pour suivre le nombre de slides visibles
    const sectionRef = useRef(null);
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const swiperRef = useRef(null);

    // Use the shared mouse parallax hook
    useMouseParallax({ elementRef: sectionRef });

    // Détecter les changements de taille d'écran pour mettre à jour slidesPerView
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            if (width < 640) {
                setSlidesPerView(1.05);
            } else if (width < 768) {
                setSlidesPerView(1.05);
            } else if (width < 1024) {
                setSlidesPerView(1.6);
            } else {
                setSlidesPerView(4.15);
            }
        };

        handleResize(); // Appliquer immédiatement
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Function to handle the "jump to first slide" behavior - Corrigée pour le mobile
    const handleNavigation = (direction) => {
        if (!swiperRef.current) return;

        const swiper = swiperRef.current.swiper;
        const lastIndex = processSteps.length - 1;
        const visibleSlideCount = Math.floor(slidesPerView);
        const effectiveLastIndex = lastIndex - (visibleSlideCount - 1);

        // Calcul du dernier index visible avant le retour au début
        const lastVisibleIndex = Math.max(0, effectiveLastIndex);

        if (direction === "next" && swiper.activeIndex >= lastVisibleIndex) {
            // Quand on atteint la dernière slide visible et qu'on clique sur next, retour au début
            swiper.slideTo(0, 800); // 800ms animation duration
        } else if (direction === "prev" && swiper.activeIndex === 0) {
            // Quand on est sur la première slide et qu'on clique sur prev, aller à la dernière
            swiper.slideTo(lastVisibleIndex, 800);
        } else {
            // Navigation normale
            direction === "next" ? swiper.slideNext() : swiper.slidePrev();
        }
    };

    return (
        <section className="pb-20 pt-5 md:pt-40 relative" ref={sectionRef}>
            {/* Modern subtle background elements */}
            <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-primary-100/20 blur-3xl floating-element opacity-50"></div>
            <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-cyan-100/30 blur-3xl floating-element opacity-50"></div>

            <div className="container mx-auto px-4 sm:p-0 lg:px-8 relative z-10">
                {/* Header section */}
                <div className="flex mx-auto justify-center items-center">
                    <div className="mb-8 max-w-2xl text-center">
                        <div className="mb-5 mt-4">
                            {/* <motion.span
                                className="bg-black/80 text-white text-xs md:text-sm px-6 py-2.5 uppercase tracking-wider font-medium shadow-lg rounded-md"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                            >
                                Notre méthodologie
                            </motion.span> */}
                        </div>
                        <div>
                            <motion.h2
                                className="text-4xl md:text-5xl font-bold text-center mb-5 title"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                            >
                                Notre{" "}
                                <span className="text-gradient">processus</span>{" "}
                                de création
                            </motion.h2>
                            <motion.p
                                className="text-gray-600 max-w-3xl mx-auto"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                De la conception à la livraison, découvrez notre
                                méthode éprouvée pour transformer votre vision
                                en réalité digitale.
                            </motion.p>
                        </div>
                    </div>
                </div>

                {/* Carousel navigation controls */}
                <div className="relative max-w-7xl mx-auto md:px-4">
                    <div className="flex justify-end space-x-2">
                        <motion.button
                            ref={prevRef}
                            className="w-12 h-12 flex items-center justify-center rounded-full bg-white border text-gray-700"
                            whileHover={{
                                scale: 1.05,
                                backgroundColor: "#f9fafb",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleNavigation("prev")}
                            aria-label="Étape précédente"
                        >
                            <ChevronLeft size={20} />
                        </motion.button>

                        <motion.button
                            ref={nextRef}
                            className="w-12 h-12 flex items-center justify-center rounded-full bg-white border text-gray-700"
                            whileHover={{
                                scale: 1.05,
                                backgroundColor: "#f9fafb",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleNavigation("next")}
                            aria-label="Étape suivante"
                        >
                            <ChevronRight size={20} />
                        </motion.button>
                    </div>

                    {/* Process cards carousel with Swiper */}
                    <div>
                        <Swiper
                            ref={swiperRef}
                            modules={[Navigation]}
                            spaceBetween={24}
                            style={{ padding: "50px 30px" }}
                            slidesPerView={slidesPerView}
                            loop={false} // Disable built-in loop
                            onSlideChange={(swiper) => {
                                setActiveIndex(swiper.activeIndex);
                            }}
                            allowTouchMove={true}
                            speed={800} // Smoother transitions
                            breakpoints={{
                                // when window width is >= 320px
                                320: {
                                    slidesPerView: 1.05,
                                    spaceBetween: 16,
                                },
                                // when window width is >= 640px
                                640: {
                                    slidesPerView: 1.05,
                                    spaceBetween: 20,
                                },
                                // when window width is >= 768px
                                768: {
                                    slidesPerView: 1.6,
                                    spaceBetween: 24,
                                },
                                // when window width is >= 1024px
                                1024: {
                                    slidesPerView: 4.15,
                                    spaceBetween: 24,
                                },
                            }}
                        >
                            {processSteps.map((step, index) => (
                                <SwiperSlide
                                    key={index}
                                    style={{ height: "380px" }}
                                >
                                    <ProcessCard step={step} isActive={true} />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProcessSection;
